/**
 * Crew positions — the single source of the position VALUES and their display
 * labels (BON-452, folding in BON-420's label consolidation).
 *
 * The backend derives its zod enum from `CREW_POSITIONS`
 * (`backend/src/lib/validation/schemas.ts`) and the frontend re-exports this
 * module (`frontend/src/lib/crewPositions.ts`), so adding a position is a
 * single edit here. Kept zod-free so this package stays dependency-light and
 * the enum is always built by the consumer's own zod instance.
 */

/** Seniority order — how crew slots are presented top to bottom. */
export const CREW_POSITIONS = ['teacher', 'assistant', 'teacher_in_training'] as const;

export type CrewPosition = (typeof CREW_POSITIONS)[number];

/** Title-case labels for chips, table cells, filters and pickers. */
export const CREW_POSITION_LABELS: Record<CrewPosition, string> = {
  teacher: 'Teacher',
  assistant: 'Assistant',
  teacher_in_training: 'Teacher in training',
};

/** Lowercase noun for mid-sentence prose ("Add a teacher in training"). */
export const CREW_POSITION_NOUNS: Record<CrewPosition, string> = {
  teacher: 'teacher',
  assistant: 'assistant',
  teacher_in_training: 'teacher in training',
};

/**
 * Label for a position value that may not be a known `CrewPosition`.
 *
 * Positions arrive from the warehouse and from FareHarbor crew rows, so an
 * unrecognized value is possible. Return it verbatim rather than guessing or
 * rendering an empty cell: an admin seeing `sensei` can act on it, whereas a
 * blank tells them nothing.
 */
export function crewPositionLabel(position: string): string {
  return CREW_POSITION_LABELS[position as CrewPosition] ?? position;
}

/** Noun form, same unknown-value contract as {@link crewPositionLabel}. */
export function crewPositionNoun(position: string): string {
  return CREW_POSITION_NOUNS[position as CrewPosition] ?? position;
}

/**
 * A viewer's part on a workshop's crew, named from their position — the "mine"
 * tag on calendar and list rows reads "Teaching", "Assisting" or "Training"
 * (BON-565). A `Record` over `CrewPosition`, so adding a position will not
 * compile until it is given a role.
 */
export const CREW_POSITION_ROLES = {
  teacher: 'teaching',
  assistant: 'assisting',
  teacher_in_training: 'training',
} as const satisfies Record<CrewPosition, string>;

export type CrewRole = (typeof CREW_POSITION_ROLES)[CrewPosition];

export const CREW_ROLE_LABELS: Record<CrewRole, string> = {
  teaching: 'Teaching',
  assisting: 'Assisting',
  training: 'Training',
};

/** The role for a position value that may not be a known `CrewPosition`;
 *  null for one it does not recognise. */
export function crewRoleOf(position: string): CrewRole | null {
  return CREW_POSITION_ROLES[position as CrewPosition] ?? null;
}
